import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SettingsEditor = () => {
  const [settings, setSettings] = useState({
    phone: '',
    email: '',
    whatsapp: '',
    telegram: '',
    address: '',
    work_hours: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings`, { headers: getAuthHeaders() });
      const settingsData = {};
      response.data.forEach(item => {
        settingsData[item.key] = item.value;
      });
      setSettings(prev => ({
        ...prev,
        ...settingsData
      }));
    } catch (error) {
      console.error('Error loading settings:', error);
      showNotification('Ошибка загрузки настроек', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Save each setting individually
      const promises = Object.entries(settings).map(([key, value]) =>
        axios.put(`${API_URL}/api/admin/settings/${key}`, { value: value || '' }, { headers: getAuthHeaders() })
      );
      
      await Promise.all(promises);
      showNotification('Настройки успешно сохранены');
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Ошибка сохранения настроек', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Настройки сайта</h1>
        <p className="text-gray-600 mt-2">Контактная информация для шапки и подвала сайта</p>
      </div>

      {notification && (
        <div className={`p-4 rounded-lg ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Contact Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
              📞 Контактная информация
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Телефон
                </label>
                <input
                  type="text"
                  name="phone"
                  value={settings.phone || ''}
                  onChange={handleInputChange}
                  placeholder="+7 (495) 123-45-67"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Основной телефон компании</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={settings.email || ''}
                  onChange={handleInputChange}
                  placeholder="mail@velox-hint.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Основной email для связи</p>
              </div>
            </div>
          </div>

          {/* Messengers Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
              💬 Мессенджеры
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp
                </label>
                <input
                  type="text"
                  name="whatsapp"
                  value={settings.whatsapp || ''}
                  onChange={handleInputChange}
                  placeholder="+79001234567"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Номер в формате: +79001234567</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telegram
                </label>
                <input
                  type="text"
                  name="telegram"
                  value={settings.telegram || ''}
                  onChange={handleInputChange}
                  placeholder="https://t.me/veloxhint"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Полная ссылка на Telegram</p>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">
              📍 Адрес и время работы
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Адрес офиса
                </label>
                <input
                  type="text"
                  name="address"
                  value={settings.address || ''}
                  onChange={handleInputChange}
                  placeholder="Москва, ул. Примерная, д. 1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Адрес для отображения в подвале</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Время работы
                </label>
                <input
                  type="text"
                  name="work_hours"
                  value={settings.work_hours || ''}
                  onChange={handleInputChange}
                  placeholder="Пн-Пт: 9:00 - 18:00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">График работы компании</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-6 border-t">
            <p className="text-sm text-gray-500">
              ✓ Изменения отобразятся на сайте сразу после сохранения
            </p>
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white rounded-lg font-medium transition"
            >
              {saving ? 'Сохранение...' : 'Сохранить настройки'}
            </button>
          </div>
        </form>

        {/* Preview Section */}
        <div className="mt-8 pt-8 border-t">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">👁️ Предпросмотр шапки сайта</h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-700">
              <div className="flex items-center space-x-2">
                <span className="text-orange-500">📞</span>
                <span>{settings.phone || '+7 (495) 123-45-67'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-orange-500">✉️</span>
                <span>{settings.email || 'mail@velox-hint.com'}</span>
              </div>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-6">👁️ Предпросмотр подвала сайта</h3>
          <div className="bg-gray-800 rounded-lg p-6 text-white">
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-2">
                <span className="text-orange-400">📞</span>
                <span className="text-gray-300">{settings.phone || '+7 (495) 123-45-67'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-orange-400">✉️</span>
                <span className="text-gray-300">{settings.email || 'mail@velox-hint.com'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-orange-400">📍</span>
                <span className="text-gray-300">{settings.address || 'Москва, Центральный офис'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-orange-400">🕐</span>
                <span className="text-gray-300">{settings.work_hours || 'Пн-Пт: 9:00 - 18:00'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Info about pages */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">ℹ️ Где ещё можно редактировать контент?</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Страницы сайта</strong> — редактирование контента главной и других страниц</li>
            <li>• <strong>Услуги</strong> — управление списком услуг компании</li>
            <li>• <strong>FAQ</strong> — часто задаваемые вопросы</li>
            <li>• <strong>Статьи</strong> — новости и статьи блога</li>
            <li>• <strong>Документы</strong> — список документов для скачивания</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SettingsEditor;
